import { recommendationScoreCalculation } from "./recommendationScoreCalculation.js"
import finishOdds from "../fantasyFootball_docs/finishOdds.json"

const oddsData = finishOdds[0]; //extract the object inside the array

export const pickPlayer = (playerList, pickedPlayerID, currentPick, currentTeamPick, currentRoundNumber) => {
    return playerList.map(player => 
    player.id === pickedPlayerID
      ? { ...player, Team: currentTeamPick, picknum: currentPick }
      : player
  );
}

export const undoLastPick = (playerList, currentPick, currentTeamPick, currentRoundNumber) => {
     // If currentPick is 1 or less, nothing to undo
  if (currentPick <= 1) return playerList;

  // Find the last picked player (picknum === currentPick - 1)
  const lastPickNum = currentPick - 1;

  // Return a new array with the last picked player reset
  const newPlayerList = playerList.map(player => {
    if (player.picknum === lastPickNum) {
      return {
        ...player,
        Team: null,
        picknum: null,
        // Optionally reset valueScore if needed:
        // valueScore: null,
      };
    }
    return player;
  });

  return newPlayerList;
}

// Math calculation functions
const injuryRiskByPosition = { //DOES NOT INCLUDE BYE WEEK
  RB: { expectedMissedGames: 2.7 },
  WR: { expectedMissedGames: 2 },
  QB: { expectedMissedGames: 1.1 },
  TE: { expectedMissedGames: 1.8 },
  K:  { expectedMissedGames: 1.8 },
  DEF: { expectedMissedGames: 0 },
};

export function predictDraftCounts(numberOfTeams, numberOfQBs, numberOfRBs, numberOfWRs, numberOfTEs, numberOfFLEXs, numberOfKs, numberOfDEFs, numberOfBenchs) {
    // Step 1: Calculate total starters
    const totalQBs = numberOfTeams * numberOfQBs;
    const totalRBs = numberOfTeams * numberOfRBs;
    const totalWRs = numberOfTeams * numberOfWRs;
    const totalTEs = numberOfTeams * numberOfTEs;
    const totalKs = numberOfTeams * numberOfKs;
    const totalDEFs = numberOfTeams * numberOfDEFs;
    const totalFLEXs = numberOfTeams * numberOfFLEXs;
    const totalBenchSpots = numberOfTeams * numberOfBenchs;

    // Step 2: FLEX distribution (RB/WR/TE)
    const flexPoolTotal = totalRBs + totalWRs + totalTEs;
    const flexRBs = totalFLEXs * (totalRBs / flexPoolTotal);
    const flexWRs = totalFLEXs * (totalWRs / flexPoolTotal);
    const flexTEs = totalFLEXs * (totalTEs / flexPoolTotal);

    // Step 3: Bench distribution (based on starter + FLEX totals)
    const benchDistributionPool =
        totalQBs +
        totalRBs + flexRBs +
        totalWRs + flexWRs +
        totalTEs + flexTEs +
        totalKs +
        totalDEFs;

    const benchQBs = totalBenchSpots * (totalQBs / benchDistributionPool);
    const benchRBs = totalBenchSpots * ((totalRBs + flexRBs) / benchDistributionPool);
    const benchWRs = totalBenchSpots * ((totalWRs + flexWRs) / benchDistributionPool);
    const benchTEs = totalBenchSpots * ((totalTEs + flexTEs) / benchDistributionPool);
    const benchKs = totalBenchSpots * (totalKs / benchDistributionPool);
    const benchDEFs = totalBenchSpots * (totalDEFs / benchDistributionPool);

    const canUseRB  = numberOfRBs  > 0 || numberOfFLEXs > 0;
    const canUseWR  = numberOfWRs  > 0 || numberOfFLEXs > 0;
    const canUseTE  = numberOfTEs  > 0 || numberOfFLEXs > 0;

    // Final predicted total drafted per position (round to nearest whole number)
    // Force 0 when the league does not use that position
    return {
      QB:  numberOfQBs  === 0 ? 0 : Math.round(totalQBs + benchQBs),
      RB:  !canUseRB          ? 0 : Math.round(totalRBs + flexRBs + benchRBs),
      WR:  !canUseWR          ? 0 : Math.round(totalWRs + flexWRs + benchWRs),
      TE:  !canUseTE          ? 0 : Math.round(totalTEs + flexTEs + benchTEs),
      K:   numberOfKs   === 0 ? 0 : Math.round(totalKs + benchKs),
      DEF: numberOfDEFs === 0 ? 0 : Math.round(totalDEFs + benchDEFs)
    };
}

function totalDraftedSlots(predictedDraftCounts) {
  return Object.values(predictedDraftCounts).reduce((sum, count) => sum + count, 0);
}

export function predictWaiverFPTS (predictedDraftCountsByPosition, playerList) {
    const result = {};

    // For each position in the predicted counts
    for (const position in predictedDraftCountsByPosition) {
        let numToDraft = predictedDraftCountsByPosition[position];
        // 1. Sort by FPTS descending
        const FPTSsort = playerList
            .filter(player => player.POS === position)
            .sort((a, b) => b.FPts - a.FPts);
        // Clamp numToDraft to the length of FPTSsort
        numToDraft = Math.min(numToDraft, FPTSsort.length);
        // 2. Get the FPTS of the last expected drafted player
        const lastFPTSPlayer = FPTSsort[numToDraft - 1]; // 0-indexed
        const FPTSresult = lastFPTSPlayer ? lastFPTSPlayer.FPts : null;
        // 3. Sort by ADP ascending (lower ADP = better draft value)
        const ADPsort = playerList
            .filter(player => player.POS === position && player.ADP !== undefined && player.ADP !== null)
            .sort((a, b) => a.ADP - b.ADP);
        // 4. Get the ADP of the last expected drafted player
        const lastADPPlayer = ADPsort[numToDraft - 1]; // 0-indexed
        const ADPresult = lastADPPlayer ? lastADPPlayer.FPts : null;
        // 5. Combine the two into a single result
        function averageIfBothExist(fpts, adp) {
            const fptsExists = typeof fpts === 'number' && fpts > 0;
            const adpExists = typeof adp === 'number' && adp > 0;

            if (fptsExists && adpExists) {
                return (fpts + adp) / 2;
            } else if (fptsExists) {
                return fpts;
            } else if (adpExists) {
                return adp;
            } else {
                return null;
            }
        }

        result[position] = Math.floor(averageIfBothExist(Number(FPTSresult), Number(ADPresult)));
    }

    return result;
}

function expectedFptsByPositionAtNextPick(playerList, nextPick) {
    const result = {};

// Group players by position
const positions = [...new Set(playerList.map(player => player.POS))];

    for (const position of positions) {
        // Filter players with valid FPts at this position
        const validPlayers = playerList.filter(
            player => player.POS === position && Number(player.FPts) > 0
        );

        // Try to find a player with ADP > nextPick
        const playersAfterNextPick = validPlayers
            .filter(player => Number(player.ADP) > nextPick)
            .sort((a, b) => a.ADP - b.ADP); // Lowest ADP first

        let nextPlayer;

        if (playersAfterNextPick.length > 0) {
            nextPlayer = playersAfterNextPick[0];
        } else {
            // Fallback: get the player with the lowest FPts at this position
            const lowestFPtsPlayer = validPlayers.sort((a, b) => a.FPts - b.FPts)[0];
            nextPlayer = lowestFPtsPlayer || null;
        }

        result[position] = nextPlayer ? Number(nextPlayer.FPts) : null;
    }

    return result;
}

function expectedNextPick(RecalculatedPick, RecalculatedCurrentTeamPick, RecalculatedRoundNumber, numberOfTeams, draftStructure) {
    let nextPick;
    if (draftStructure === 'snake') {
        const isEvenRound = RecalculatedRoundNumber % 2 === 0;

        if (!isEvenRound) {
            // Reversed order
            nextPick = (RecalculatedRoundNumber + 1) * numberOfTeams - RecalculatedCurrentTeamPick + 1;
        } else {
            // Normal order
            nextPick = RecalculatedRoundNumber * numberOfTeams + RecalculatedCurrentTeamPick;
        }
    } else {
        // Linear draft
        nextPick = RecalculatedPick + numberOfTeams;
    }
    return(nextPick)
}

export function roundTo(value, decimals) {
    return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals);
}

function toNumber(val) {
    return parseFloat(val) || 0;
}

export function getTeamRosterBreakdown({
  playerList,
  RecalculatedCurrentTeamPick,
  numberOfQBs,
  numberOfRBs,
  numberOfWRs,
  numberOfTEs,
  numberOfKs,
  numberOfDEFs,
  numberOfFLEXs
}) {
  const filterAndSort = (pos, limit) =>
    playerList
      .filter(p => p.Team === RecalculatedCurrentTeamPick && p.POS === pos)
      .sort((a, b) => b.FPts - a.FPts)
      .slice(0, limit);

  const topQBs = filterAndSort('QB', numberOfQBs);
  const topRBs = filterAndSort('RB', numberOfRBs);
  const topWRs = filterAndSort('WR', numberOfWRs);
  const topTEs = filterAndSort('TE', numberOfTEs);
  const topKs  = filterAndSort('K', numberOfKs);
  const topDEFs = filterAndSort('DEF', numberOfDEFs);

  const draftedCounts = {
    QB: playerList.filter(p => p.Team === RecalculatedCurrentTeamPick && p.POS === 'QB').length,
    RB: playerList.filter(p => p.Team === RecalculatedCurrentTeamPick && p.POS === 'RB').length,
    WR: playerList.filter(p => p.Team === RecalculatedCurrentTeamPick && p.POS === 'WR').length,
    TE: playerList.filter(p => p.Team === RecalculatedCurrentTeamPick && p.POS === 'TE').length,
    K:  playerList.filter(p => p.Team === RecalculatedCurrentTeamPick && p.POS === 'K').length,
    DEF: playerList.filter(p => p.Team === RecalculatedCurrentTeamPick && p.POS === 'DEF').length,
  };

  const shownPlayerIDs = new Set([
    ...topQBs,
    ...topRBs,
    ...topWRs,
    ...topTEs,
  ].map(p => p.id));

  const topFLEXs = playerList
    .filter(p =>
      p.Team === RecalculatedCurrentTeamPick &&
      ['RB', 'WR', 'TE'].includes(p.POS) &&
      !shownPlayerIDs.has(p.id)
    )
    .sort((a, b) => b.FPts - a.FPts)
    .slice(0, numberOfFLEXs);

  const shownAllPlayerIDs = new Set([
    ...topQBs,
    ...topRBs,
    ...topWRs,
    ...topTEs,
    ...topFLEXs,
    ...topKs,
    ...topDEFs,
  ].map(p => p.id));

  const benchPlayers = playerList
    .filter(p =>
      p.Team === RecalculatedCurrentTeamPick &&
      !shownAllPlayerIDs.has(p.id)
    )
    .sort((a, b) => b.FPts - a.FPts);

  return {
    topQBs,
    topRBs,
    topWRs,
    topTEs,
    topKs,
    topDEFs,
    topFLEXs,
    benchPlayers,
    draftedCounts
  };
}

function getFptsByPositionADP(players, rank) {
  const toNumber = (val) => parseFloat(val) || 0;
  const positions = ["QB", "RB", "WR", "TE"];
  const result = {};

  positions.forEach(pos => {
    const topX = players
      .filter(p => p.POS === pos)
      .sort((a, b) => toNumber(b.originalFPts) - toNumber(a.originalFPts)) // ascending ADP = earlier pick
      .slice(0, rank);

    const XPlayer = topX[rank-1];
    result[pos] = XPlayer ? toNumber(XPlayer.FPts) : null;
  });

  return result;
}

// Player FPTS Adjustment Functions

function adjustQB_StyleBonuses(players) {
  const qbs = players.filter(player => player.POS === "QB");

  const toNumber = (val) => {
    const num = parseFloat(val);
    return isNaN(num) ? 0 : num;
  };

  // Bonus Metrics
  const topRushAtt = [...qbs]  // Sort and get top 3 in Rush_Att (descending)
    .sort((a, b) => toNumber(b["Rush_Att"]) - toNumber(a["Rush_Att"]))
    .slice(0, 5)
    .map(p => p.Player);

  const topRushYds = [...qbs] // Sort and get top 3 in Rush_Yds (descending)
    .sort((a, b) => toNumber(b["Rush_Yds"]) - toNumber(a["Rush_Yds"]))
    .slice(0, 5)
    .map(p => p.Player);

  const topHighInts = [...qbs] // Sort and get top 3 in INT (descending, i.e. high projected interceptions get a bonus)
    .sort((a, b) => toNumber(b["INT"]) - toNumber(a["INT"])) // highest ints
    .slice(0, 3)
    .map(p => p.Player);

  // Penalty Metrics
  const topPassYds = [...qbs] // Sort and get top 3 in Pass_Yds (descending)
    .sort((a, b) => toNumber(b["Pass_Yds"]) - toNumber(a["Pass_Yds"]))
    .slice(0, 5)
    .map(p => p.Player);

  const topCmpPct = [...qbs] // Sort and get top 3 in cmp% (descending)
    .sort((a, b) => toNumber(b["cmp%"]) - toNumber(a["cmp%"]))
    .slice(0, 5)
    .map(p => p.Player);

  const topLowInts = [...qbs] // Sort and get bottom 3 in INT (ascending, i.e. low projected interceptions get dinged)
    .sort((a, b) => toNumber(a["INT"]) - toNumber(b["INT"])) // fewest ints
    .slice(0, 5)
    .map(p => p.Player);

  // Apply bonus/penalty
  return players.map(player => {
    if (player.POS !== "QB") return player;

    let multiplier = 1;

    // Bonuses
    if (topRushAtt.includes(player.Player)) multiplier *= 1.025;
    if (topRushYds.includes(player.Player)) multiplier *= 1.025;
    if (topHighInts.includes(player.Player)) multiplier *= 1.025;

    // Penalties
    if (topPassYds.includes(player.Player)) multiplier *= 0.95;
    if (topCmpPct.includes(player.Player)) multiplier *= 0.95;
    if (topLowInts.includes(player.Player)) multiplier *= 0.95;

    const currentBonus = toNumber(player.FptsBonus);
    const newBonus = (currentBonus * multiplier).toFixed(6);

    return {
      ...player,
      FptsBonus: newBonus
    };
  });
}

function penalizeTop5RBs(players) {
  const toNumber = (val) => parseFloat(val) || 0;

  // Find top 5 RBs by FPts
  const topRBs = players
    .filter(player => player.POS === "RB")
    .sort((a, b) => toNumber(b.FPts) - toNumber(a.FPts))
    .slice(0, 5);

  // Check if any of the top 5 RBs are undrafted
  const anyTop5RBsAvailable = topRBs.some(player => player.Team === null);

  // Build a penalty map: player ID => penalty multiplier
  const penaltyMap = {};
  if (anyTop5RBsAvailable) {
    topRBs.forEach((player, index) => {
      const penaltyPercent = 0.05 - (index * 0.01);  // 5%, 4%, ..., 1%
      penaltyMap[player.PlayerID] = 1 - penaltyPercent;
    });
  }

  // Apply penalties to the appropriate RBs
  return players.map(player => {
    if (player.POS !== "RB") return player;

    const currentBonus = toNumber(player.FptsBonus);
    const penaltyMultiplier = penaltyMap[player.PlayerID] || 1;

    return {
      ...player,
      FptsBonus: roundTo(currentBonus * toNumber(penaltyMultiplier), 2)
    };
  });
}

function penalizeLowRankedWRs(players) {
  const toNumber = (val) => parseFloat(val) || 0;

  // Sort WRs by FPts (highest to lowest)
  const sortedWRs = players
    .filter(player => player.POS === "WR")
    .sort((a, b) => toNumber(b.FPts) - toNumber(a.FPts));

  // Create a map of WR name => penaltyPercent
  const penaltyMap = new Map();

  sortedWRs.forEach((player, index) => {
    if (index >= 30) {
      const penaltyPercent = Math.min(index - 29, 10); // 1% for WR31, up to 10%
      penaltyMap.set(player.Player, penaltyPercent);
    }
  });

  // Apply the penalties
  return players.map(player => {
    if (player.POS !== "WR") return player;

    const currentBonus = toNumber(player.FptsBonus);
    const penaltyPercent = penaltyMap.get(player.Player) || 0;
    const multiplier = 1 - penaltyPercent / 100;
    const newBonus = (currentBonus * multiplier).toFixed(6);

    return {
      ...player,
      FptsBonus: newBonus
    };
  });
}

function injuryRiskFPTSadjustment(players) {
  const toNumber = (val) => {
    const num = parseFloat(val);
    return isNaN(num) ? 0 : num;
  };

  return players.map(player => {
    const pos = player.POS;
    const riskData = injuryRiskByPosition[pos];
    const currentBonus = toNumber(player.FptsBonus);

    const FptsBonus =
    riskData && typeof riskData.expectedMissedGames === 'number'
    ? 1 - riskData.expectedMissedGames / 17
    : 1; // Default to no penalty if position is unknown

    return {
    ...player,
    FptsBonus: roundTo(currentBonus * FptsBonus, 2), // or keep full precision if needed
    };
  });
}

function applyFptsBonus(players) {
  const toNumber = (val) => parseFloat(val) || 0;

  return players.map(player => {
    const fpts = toNumber(player.originalFPts);
    const bonus = toNumber(player.FptsBonus) || 1;

    return {
      ...player,
      FPts: roundTo(fpts * bonus, 2),
      FptsBonus: roundTo(bonus, 2)
    };
  });
}

// Value Score Adjustment Functions

function adjustQB_roundBonuses(players, RecalculatedPick, RecalculatedCurrentTeamPick, RecalculatedRoundNumber, numberOfTeams, numberOfQBs) {
    const round = RecalculatedRoundNumber;
    
    // In multi-QB leagues, skip QB round-based bonus logic
    if (numberOfQBs>1) return players

    // Helper to parse ADP safely
    const toNumber = (val) => parseFloat(val) || 0;

    return players.map(player => {
        if (player.POS !== "QB") return player;

        const ADP = toNumber(player.ADP);
        let adjustmentFactor = 0;

        switch (round) {
        case 1:
        case 2:
            adjustmentFactor = ADP - RecalculatedPick + numberOfTeams;
            break;
        case 3:
            adjustmentFactor = ADP - RecalculatedPick + numberOfTeams / 2;
            break;
        case 4:
            adjustmentFactor = ADP - RecalculatedPick;
            break;
        case 5:
            adjustmentFactor = ADP - RecalculatedPick - numberOfTeams / 2;
            break;
        case 6:
            adjustmentFactor = ADP - RecalculatedPick - numberOfTeams;
            break;
        case 7:
            adjustmentFactor = ADP - RecalculatedPick - numberOfTeams;
            break;
        case 8:
            adjustmentFactor = ADP - RecalculatedPick - numberOfTeams / 2;
            break;
        case 9:
            adjustmentFactor = ADP - RecalculatedPick;
            break;
        case 10:
            adjustmentFactor = ADP - RecalculatedPick + numberOfTeams / 2;
            break;
        case 11:
            adjustmentFactor = ADP - RecalculatedPick + numberOfTeams;
            break;
        default:
            if (round >= 12) {
            adjustmentFactor = ADP - RecalculatedPick + 2 * numberOfTeams;
            }
            break;
        }

        let penalty = 1 + (adjustmentFactor > 0 ? -0.2 : -0.22) * (adjustmentFactor / numberOfTeams);
        penalty = Math.min(1.50, Math.max(0.65, penalty)); // Clamp between -35% and +50%
        const newBonus = (toNumber(player.DraftScoreBonus) * (penalty)).toFixed(6);

        return {
        ...player,
        DraftScoreBonus: roundTo(newBonus,2)
        };
    });
}

function adjustTopWRBonus(players) {
  const toNumber = (val) => parseFloat(val) || 0;

  // Get the top 25 WRs by fantasy points
  const topWRs = players
    .filter(player => player.POS === "WR")
    .sort((a, b) => toNumber(b.FPts) - toNumber(a.FPts))
    .slice(0, 25);

  // Map player name to bonus multiplier
  const wrBonusMap = {};
  topWRs.forEach((player, index) => {
    if (index < 15) {
      wrBonusMap[player.Player] = 1.25; // 15% bonus
    } else {
      const drop = (index - 14) * 0.025; // starts from WR16
      const bonus = 1.15 - drop;
      wrBonusMap[player.Player] = Math.max(bonus, 1); // Never go below 1.0 (no bonus)
    }
  });

  // Apply bonuses
  return players.map(player => {
    if (player.POS !== "WR") return player;

    const currentBonus = toNumber(player.DraftScoreBonus);
    const multiplier = wrBonusMap[player.Player] || 1;

    return {
      ...player,
      DraftScoreBonus: roundTo(currentBonus * multiplier, 2),
    };
  });
}

function adjustForFLEXImbalance(players, predictedDraftCountsByPosition) {
  const toNumber = (val) => parseFloat(val) || 0;

  // Step 1: Filter out DEF and K
  const filteredPositions = Object.keys(predictedDraftCountsByPosition).filter(
    pos => pos !== "DEF" && pos !== "K"
  );

  // Step 2: Calculate total expected count from filtered positions
  const totalExpected = filteredPositions.reduce(
    (sum, pos) => sum + predictedDraftCountsByPosition[pos],
    0
  );
  // Step 3: Compute expected ratio per position
  const expectedRatios = {};
  for (const pos of filteredPositions) {
    expectedRatios[pos] = predictedDraftCountsByPosition[pos] / totalExpected;
  }
  // Step 4: Count actual drafted players per position
  const draftedCounts = {};
  let totalDrafted = 0;
  for (const pos in predictedDraftCountsByPosition) {
    draftedCounts[pos] = players.filter(p => p.POS === pos && p.picknum != null).length;
    totalDrafted += draftedCounts[pos];
  }

  if (totalDrafted === 0) return players; // Nothing drafted yet

  // Step 5: Compute bonus multipliers
  const bonusMultipliers = {};
  for (const pos in expectedRatios) {
    const expectedCount = expectedRatios[pos] * totalDrafted;
    const actualCount = draftedCounts[pos] || 0;

    bonusMultipliers[pos] =
      actualCount > 0 && actualCount < expectedCount
        ? 1 + 0.05 * (expectedCount - actualCount) / actualCount
        : 1;
  }

  // Step 6: Apply bonuses
  return players.map(player => {
    const currentBonus = toNumber(player.DraftScoreBonus);
    const pos = player.POS;

    if (!bonusMultipliers[pos]) return player; // Skip if no multiplier for this position

    const multiplier = bonusMultipliers[pos];
    const adjustedBonus = currentBonus * multiplier;

    return {
      ...player,
      DraftScoreBonus: roundTo(adjustedBonus.toFixed(6), 2),
    };
  });
}

function adjustBonusByADP(players, RecalculatedPick, numberOfTeams) {
  const toNumber = (val) => parseFloat(val) || 0;

  return players.map(player => {
    const currentDraftScoreBonus = toNumber(player.DraftScoreBonus);
    const playerADP = toNumber(player.ADP);

    if (!playerADP || playerADP <= 0) {
      return player; // Skip if no valid ADP
    }

    const roundDiff = (RecalculatedPick - playerADP)/numberOfTeams; // positive = drafting LATE (bonus), negative = EARLY (penalty)

    const fullRoundsDiff = Math.sign(roundDiff) * ( Math.max( Math.abs(roundDiff) - 0.5 , 0 ) );
    let adjustmentFactor = 1 + fullRoundsDiff * ( fullRoundsDiff > 0 ? 0.25 : 0.175 );
    adjustmentFactor = Math.min(1.50, Math.max(0.65, adjustmentFactor)); // Clamp between -35% and +50%

    return {
      ...player,
      DraftScoreBonus: roundTo(currentDraftScoreBonus * adjustmentFactor, 2),
    };
  });
}

function applyForcedNeedBonus(playerList,RecalculatedPick,RecalculatedCurrentTeamPick,RecalculatedRoundNumber,numberOfTeams,draftStructure,numberOfQBs,numberOfRBs,numberOfWRs,numberOfTEs,numberOfFLEXs,numberOfKs,numberOfDEFs,numberOfBenchs) {
    const totalRounds = numberOfQBs + numberOfRBs + numberOfWRs + numberOfTEs + numberOfFLEXs + numberOfKs + numberOfDEFs + numberOfBenchs;
    const picksPerTeam = totalRounds;
    const picksMadeByTeam = playerList.filter(p => p.Team === RecalculatedCurrentTeamPick).length;
    const remainingPicks = picksPerTeam - picksMadeByTeam;

    const draftedCounts = playerList.reduce((acc, player) => {
        if (player.Team !== RecalculatedCurrentTeamPick) return acc;
        acc[player.POS] = (acc[player.POS] || 0) + 1;
        return acc;
    }, {});

    const positionRequirements = {
        QB: numberOfQBs,
        RB: numberOfRBs,
        WR: numberOfWRs,
        TE: numberOfTEs,
        K: numberOfKs,
        DEF: numberOfDEFs
        // FLEX is handled separately below
    };

    // Count unfilled essential positions
    let unfilledEssentialPositions = [];

    for (const pos in positionRequirements) {
        const drafted = draftedCounts[pos] || 0;
        const missing = positionRequirements[pos] - drafted;

        for (let i = 0; i < missing; i++) {
            unfilledEssentialPositions.push(pos);
        }
    }

    // Count total drafted RB, WR, TE for FLEX calculation
    const flexRBs = Math.max(draftedCounts['RB'] - numberOfRBs, 0)
    const flexWRs = Math.max(draftedCounts['WR'] - numberOfWRs, 0)
    const flexTEs = Math.max(draftedCounts['TE'] - numberOfTEs, 0)
    if (flexRBs+flexWRs+flexTEs < numberOfFLEXs)  {
        const missing = numberOfFLEXs - (flexRBs+flexWRs+flexTEs);
        for (let i = 0; i < missing; i++) {
            unfilledEssentialPositions.push('FLEX');
        }
    }

    const uniqueUnfilled = [...new Set(unfilledEssentialPositions)];

    if (uniqueUnfilled.length === remainingPicks) {
        return playerList.map(player => {
        const isAvailable = player.Team == null;
        const isNeededPosition = (uniqueUnfilled.includes(player.POS) || (uniqueUnfilled.includes('FLEX') && ['RB', 'WR', 'TE'].includes(player.POS)))
        if (isAvailable && isNeededPosition) {
            return {
            ...player,
            DraftScoreBonus: 100
            };
        }
        return player;
        });
    }

    return playerList;
}

function comparisonFPTSadjustmentFLEX (playerList, averagePredictedFPTS, numberOfQBs, numberOfRBs, numberOfWRs, numberOfTEs, numberOfFLEXs, numberOfKs, numberOfDEFs, numberOfBenchs, RecalculatedCurrentTeamPick) {
  const {topQBs,topRBs,topWRs,topTEs,topKs,topDEFs,topFLEXs,benchPlayers,draftedCounts} = getTeamRosterBreakdown({playerList,RecalculatedCurrentTeamPick,numberOfQBs,numberOfRBs,numberOfWRs,numberOfTEs,numberOfKs,numberOfDEFs,numberOfFLEXs});
  const flexPositions = ['RB', 'WR', 'TE'];
    const positionTopCounts = {
      RB: topRBs.length,
      WR: topWRs.length,
      TE: topTEs.length,
    };
    const positionMaxSlots = {
      RB: numberOfRBs,
      WR: numberOfWRs,
      TE: numberOfTEs,
    };

    // Get highest predicted FLEX comparison baseline
    const highestFlexAvgFPTS = Math.max(
      averagePredictedFPTS.RB || 0,
      averagePredictedFPTS.WR || 0,
      averagePredictedFPTS.TE || 0
    );

    return playerList.map(player => {
      const pos = player.POS;
      let comparisonFpts = player.comparisonFpts ?? 0;

      // Check if FLEX-eligible and position is already "full"
      if (
        flexPositions.includes(pos) &&
        positionTopCounts[pos] >= positionMaxSlots[pos]
      ) {
        comparisonFpts = highestFlexAvgFPTS;
      }

      return {
        ...player,
        comparisonFpts: roundTo(comparisonFpts, 2),
      };
    });
}

function overPerformanceChanceAdjustment(players, predictedWaiverFPTS) {
  const tier1_fpts = getFptsByPositionADP(players, 1)
  const tier2_fpts = getFptsByPositionADP(players, 13)
  const tier3_fpts = getFptsByPositionADP(players, 25)
  const tier4_fpts = getFptsByPositionADP(players, 37)

  return players.map(player => {
    const pos = player.POS;
    const FPTS = player.FPts;
    const comparisonFpts = player.comparisonFpts;
    const expectedDraftRound = Math.min(player.ADP / 12, 20) //the odds stats are based off a 12 player league
    let overPerformancePoints = 0;
    const skillPositions = ["QB", "RB", "WR", "TE"];

    if (skillPositions.includes(player.POS)){
      const tier1odds = oddsData[pos]["1"]["a"] * Math.pow(2.71828 , oddsData[pos]["1"]["b"] * expectedDraftRound)
      const tier2odds = oddsData[pos]["2"]["a"] * Math.pow(2.71828 , oddsData[pos]["2"]["b"] * expectedDraftRound)
      const tier3odds = oddsData[pos]["3"]["a"] * Math.pow(2.71828 , oddsData[pos]["3"]["b"] * expectedDraftRound)
      const tier4odds = oddsData[pos]["4"]["a"] * Math.pow(2.71828 , oddsData[pos]["4"]["b"] * expectedDraftRound)

      //normalize
      const tier1odds_corr = tier1odds / (tier1odds + tier2odds + tier3odds + tier4odds)
      const tier2odds_corr = tier2odds / (tier1odds + tier2odds + tier3odds + tier4odds)
      const tier3odds_corr = tier3odds / (tier1odds + tier2odds + tier3odds + tier4odds)
      const tier4odds_corr = tier4odds / (tier1odds + tier2odds + tier3odds + tier4odds)

      overPerformancePoints = tier1odds_corr * tier1_fpts[pos] + tier2odds_corr * tier2_fpts[pos] + tier3odds_corr * tier3_fpts[pos] + tier4odds_corr * tier4_fpts[pos] - comparisonFpts
    }

    return {
      ...player,
      overPerformancePoints: overPerformancePoints
    };
     
  })
}





export const calculatePlayerValues = (playerList, RecalculatedPick, RecalculatedCurrentTeamPick, RecalculatedRoundNumber, numberOfTeams, draftStructure, numberOfQBs, numberOfRBs, numberOfWRs, numberOfTEs, numberOfFLEXs, numberOfKs, numberOfDEFs, numberOfBenchs) => {
    // calculating the VBD score
    // calculating delta between this pick and the next pick, 2rds from now pick, waiver wire pick

    playerList.forEach(player => {
      player.FptsBonus = 1;
    });

    const playerFPTSAdjustmentFunctions = [
        (players) => adjustQB_StyleBonuses(players), // BONUS 1 - adds +3% for high rushing projections, and -3% for high pass projection
        (players) => penalizeTop5RBs(players), // BONUS 5 - punish high ranked RBs
        (players) => penalizeLowRankedWRs(players), // BONUS 4 - WRs below rank 30 are incredibly over-projected and should be avoided if possible
        //(players) => injuryRiskFPTSadjustment(players), //FPTS bonus reduction by expected missed games for each player

        (players) => applyFptsBonus(players) //apply all FPTS adjustment functions
    ]

    const playerList2 = playerFPTSAdjustmentFunctions.reduce(
        (playersSoFar, bonusFn) => bonusFn(playersSoFar),
         playerList// <-- this is your starting list
    );
    
    const predictedDraftCountsByPosition = predictDraftCounts(numberOfTeams, numberOfQBs, numberOfRBs, numberOfWRs, numberOfTEs, numberOfFLEXs, numberOfKs, numberOfDEFs, numberOfBenchs);
    const predictedWaiverFPTS = predictWaiverFPTS(predictedDraftCountsByPosition, playerList2);
    const predictedNextPickFPTS = expectedFptsByPositionAtNextPick(playerList2, expectedNextPick(RecalculatedPick, RecalculatedCurrentTeamPick, RecalculatedRoundNumber, numberOfTeams, draftStructure))
    const predicted3roundFutureFPTS = expectedFptsByPositionAtNextPick(playerList2, RecalculatedPick+numberOfTeams*3)

    const averagePredictedFPTS = {
      QB:(2* predictedWaiverFPTS['QB'] + predictedNextPickFPTS['QB'] + 2* predicted3roundFutureFPTS['QB'])/5,
      RB:(2* predictedWaiverFPTS['RB'] + predictedNextPickFPTS['RB'] + 2* predicted3roundFutureFPTS['RB'])/5,
      WR:(2* predictedWaiverFPTS['WR'] + predictedNextPickFPTS['WR'] + 2* predicted3roundFutureFPTS['WR'])/5,
      TE:(2* predictedWaiverFPTS['TE'] + predictedNextPickFPTS['TE'] + 2* predicted3roundFutureFPTS['TE'])/5,
      K:(2* predictedWaiverFPTS['K'] + predictedNextPickFPTS['K'] + 2* predicted3roundFutureFPTS['K'])/5,
      DEF:(2* predictedWaiverFPTS['DEF'] + predictedNextPickFPTS['DEF'] + 2* predicted3roundFutureFPTS['DEF'])/5,
    }

    // Step 2: Update playerList with valueScore and bonus
    const updatedPlayerList = playerList2.map( player => {
      const pos = player.POS;
      const average = averagePredictedFPTS[pos];
      return {
        ...player,
        DraftScoreBonus:1,
        comparisonFpts: average,
        gamesExpectedInStartinLineup: 0
      };
    });

    const playerBonusFunctions = [
        
        (players) => adjustQB_roundBonuses(players, RecalculatedPick, RecalculatedCurrentTeamPick, RecalculatedRoundNumber, numberOfTeams, numberOfQBs), // BONUS 2 - bonuses/reductions based on ADP vs current pick
        (players) => adjustTopWRBonus(players), // BONUS 3 - top 25 WRs get a bonus because they frequently finish in the top 5 (20%)
        (players) => adjustForFLEXImbalance(players, predictedDraftCountsByPosition), // BONUS 6 - maintain RB:WR balance of 4:6
        (players) => adjustBonusByADP(players, RecalculatedPick, numberOfTeams), //bonus for players fallen far below their ADP, and detriment for players that are still not expected to be picked
        
        (players) => comparisonFPTSadjustmentFLEX(players, averagePredictedFPTS, numberOfQBs, numberOfRBs, numberOfWRs, numberOfTEs, numberOfFLEXs, numberOfKs, numberOfDEFs, numberOfBenchs, RecalculatedCurrentTeamPick), //comparison FPTS adjustment function (for FLEX)
        (players) => overPerformanceChanceAdjustment(players, predictedWaiverFPTS), //adjust for overperformance odds of skill positions
        (players) => applyForcedNeedBonus(players,RecalculatedPick,RecalculatedCurrentTeamPick,RecalculatedRoundNumber,numberOfTeams,draftStructure,numberOfQBs,numberOfRBs,numberOfWRs,numberOfTEs,numberOfFLEXs,numberOfKs,numberOfDEFs,numberOfBenchs), //apply major bonus to critical positional shortages
        (players) => recommendationScoreCalculation(players, RecalculatedPick, RecalculatedCurrentTeamPick, RecalculatedRoundNumber, numberOfTeams, draftStructure, numberOfQBs, numberOfRBs, numberOfWRs, numberOfTEs, numberOfFLEXs, numberOfKs, numberOfDEFs, numberOfBenchs, averagePredictedFPTS, injuryRiskByPosition), //adjust bonuses based on current team composition & score modifiers

    ]

    // Apply each bonus function in sequence
    const finalPlayersList = playerBonusFunctions.reduce(
        (playersSoFar, bonusFn) => bonusFn(playersSoFar),
        updatedPlayerList // <-- this is your starting list
    );

    // Zero recommendation scores only for positions that have *no* possible use
    const canUseRB  = numberOfRBs  > 0 || numberOfFLEXs > 0;
    const canUseWR  = numberOfWRs  > 0 || numberOfFLEXs > 0;
    const canUseTE  = numberOfTEs  > 0 || numberOfFLEXs > 0;

    const positionStillUsable = {
      QB:  numberOfQBs  > 0,
      RB:  canUseRB,
      WR:  canUseWR,
      TE:  canUseTE,
      K:   numberOfKs   > 0,
      DEF: numberOfDEFs > 0
    };

    return finalPlayersList.map(p => {
      if (!positionStillUsable[p.POS]) {
        return {
          ...p,
          recommend: 0,
          valueScore: 0
        };
      }
      return p;
    });

}

